import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import sendErrorReport from 'common/utils/sendErrorReport';
import { Props as ImageUploadInputProps } from 'modules/menu/components/ImageUploadInput';
import useAssetRepositoryService from 'modules/shared-menu/hooks/useAssetRepositoryService';

import { MAX_IMAGE_FILE_SIZE } from '../constants';

export class ImageProcessingError extends Error {}

export const useProcessSelectionImage = () => {
  const intl = useIntl();
  const { uploadAsset } = useAssetRepositoryService();

  return useCallback(
    async (value: ImageUploadInputProps['value']): Promise<string | null> => {
      switch (value.type) {
        case 'existing-image': {
          return value.imageUrl;
        }
        case 'new-image': {
          if (!validateFileSize(value.file, MAX_IMAGE_FILE_SIZE)) {
            throw new ImageProcessingError(intl.formatMessage({ id: 'image-upload.errorProcessingFailed' }));
          }

          let imageUrl;
          try {
            imageUrl = await uploadAsset(
              new File([value.file], cleanFileName(value.file.name), { type: value.file.type }),
            );
          } catch (error) {
            sendErrorReport(error, 'Uploading image failed');
            throw new ImageProcessingError(intl.formatMessage({ id: 'image-upload.errorUploadFailed' }));
          }

          return imageUrl;
        }
        case 'no-image': {
          return null;
        }
      }
    },
    [intl, uploadAsset],
  );
};

const changeExtensionToJpeg = (fileName: string): string => {
  const fileNameRegex = /^(.+\.)([^.]+)$/i;
  const matches = fileNameRegex.exec(fileName);
  if (matches) {
    return matches[1] + 'jpeg';
  } else {
    return fileName + '.jpeg';
  }
};

const removeNonStandardChars = (fileName: string): string => {
  const invalidCharsRegex = /[^A-Za-z0-9._\-]/g;
  return fileName.replace(invalidCharsRegex, '_');
};

export const cleanFileName = (name: string) => changeExtensionToJpeg(removeNonStandardChars(name));

export const validateFileSize = (file: File, max: number) => {
  return file.size <= max;
};
